import { create } from 'zustand';
import { createDemoState, type DemoState } from '@/demo/demoData';

const STORAGE_KEY = 'ludolio.teacher.demo.v2';

interface PersistedShape {
  isDemoMode: boolean;
  data: DemoState;
}

interface DemoStoreState {
  isDemoMode: boolean;
  data: DemoState | null;
  enterDemo: () => DemoState;
  exitDemo: () => void;
  updateData: (mutator: (draft: DemoState) => void) => void;
}

const isRecord = (value: unknown): value is Record<string, string[]> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const normalizeDemoState = (data: Partial<DemoState> | null | undefined): DemoState => {
  const fresh = createDemoState();
  if (!data || typeof data !== 'object') return fresh;

  return {
    ...fresh,
    ...data,
    currentUser: data.currentUser ?? fresh.currentUser,
    teachers: Array.isArray(data.teachers) ? data.teachers : fresh.teachers,
    students: Array.isArray(data.students) ? data.students : fresh.students,
    games: Array.isArray(data.games) ? data.games : fresh.games,
    units: Array.isArray(data.units) ? data.units : fresh.units,
    unitStudentIds: isRecord(data.unitStudentIds) ? data.unitStudentIds : fresh.unitStudentIds,
    unitTeacherIds: isRecord(data.unitTeacherIds) ? data.unitTeacherIds : fresh.unitTeacherIds,
    unitGameIds: isRecord(data.unitGameIds) ? data.unitGameIds : fresh.unitGameIds,
    pendingEnrollments: Array.isArray(data.pendingEnrollments) ? data.pendingEnrollments : fresh.pendingEnrollments,
    achievements: Array.isArray(data.achievements) ? data.achievements : fresh.achievements,
    userAchievements: Array.isArray(data.userAchievements) ? data.userAchievements : fresh.userAchievements,
    assignments: Array.isArray(data.assignments) ? data.assignments : fresh.assignments,
    activityLibraryItems: Array.isArray(data.activityLibraryItems) ? data.activityLibraryItems : fresh.activityLibraryItems,
    activities: Array.isArray(data.activities) ? data.activities : fresh.activities,
    activityResponses: Array.isArray(data.activityResponses) ? data.activityResponses : fresh.activityResponses,
  };
};

const readSession = (): PersistedShape | null => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    if (!parsed?.isDemoMode) return null;
    return { isDemoMode: true, data: normalizeDemoState(parsed.data) };
  } catch { return null; }
};

const writeSession = (value: PersistedShape | null) => {
  if (typeof sessionStorage === 'undefined') return;
  try { if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); else sessionStorage.removeItem(STORAGE_KEY); } catch {}
};

const initial = readSession();

export const useDemoStore = create<DemoStoreState>((set, get) => ({
  isDemoMode: initial?.isDemoMode ?? false,
  data: initial?.data ?? null,
  enterDemo: () => {
    const fresh = createDemoState();
    set({ isDemoMode: true, data: fresh });
    writeSession({ isDemoMode: true, data: fresh });
    return fresh;
  },
  exitDemo: () => {
    set({ isDemoMode: false, data: null });
    writeSession(null);
  },
  updateData: (mutator) => {
    const current = get().data;
    if (!current) return;
    const next = JSON.parse(JSON.stringify(current)) as DemoState;
    mutator(next);
    set({ data: next });
    writeSession({ isDemoMode: true, data: next });
  },
}));